import { create } from 'zustand';
import { mmkv } from '@/storage/mmkv';
import type { Tier } from '@/policy/limits';

const TIER_KEY = 'sub_tier';

interface SubscriptionState {
  tier: Tier;
  creditsRemaining: number;
  /**
   * Whether the balance above came from the server.
   *
   * It starts at 0, which is not a balance — it is "nobody has asked yet". The pill read it
   * anyway, so the first thing a signed-in person saw was "0 CREDITS": an account that had
   * done nothing wrong being told it could do nothing. Only a generation, or a visit to
   * Account, ever replaced it.
   */
  creditsKnown: boolean;
  anonPromptsUsed: number;
  devBypass: boolean;
  setTier: (tier: Tier) => void;
  setCredits: (credits: number) => void;
  bumpAnonPrompt: () => void;
  setDevBypass: (on: boolean) => void;
  reset: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  tier: (mmkv.getString(TIER_KEY) as Tier) || 'free',
  creditsRemaining: 0,
  creditsKnown: false,
  anonPromptsUsed: 0,
  devBypass: false,

  setTier: (tier) => {
    mmkv.setString(TIER_KEY, tier);
    set({ tier });
  },
  setCredits: (creditsRemaining) => set({ creditsRemaining, creditsKnown: true }),
  bumpAnonPrompt: () => set((s) => ({ anonPromptsUsed: s.anonPromptsUsed + 1 })),
  setDevBypass: (devBypass) => set({ devBypass }),
  reset: () => {
    mmkv.delete(TIER_KEY);
    set({ tier: 'free', creditsRemaining: 0, creditsKnown: false, anonPromptsUsed: 0, devBypass: false });
  },
}));
